import { ElMessageBox } from 'element-plus'
import { Ref } from 'vue'
import type { ComposerTranslation } from 'vue-i18n'
import { EarlyWarningFrequency, EarlyWarningAndMonitoringSettings } from '../../group-policy-settings.service'

export function parseHourTime (time: number): number {
  // eslint-disable-next-line no-magic-numbers
  return time / 60 / 60
}

export function parseHourTimeToTimestamp (time: number): number {
  // eslint-disable-next-line no-magic-numbers
  return time * 60 * 60
}

// eslint-disable-next-line no-magic-numbers
export const DEFAULT_MORNING_RANGE = [8, 12]
// eslint-disable-next-line no-magic-numbers
export const DEFAULT_AFTERNOON_RANGE = [14, 18]

export const FREQUENCY_OPTIONS = [
  {
    label: '30',
    value: EarlyWarningFrequency.ThirtyMin
  },
  {
    label: '60',
    value: EarlyWarningFrequency.SixtyMin
  },
  {
    label: '120',
    value: EarlyWarningFrequency.OneHundredAndTwentyMin
  },
  {
    label: '180',
    value: EarlyWarningFrequency.OneHundredAndEightyMin
  }
]

export const DEFAULT_PARAMS = {
  frequency: EarlyWarningFrequency.ThirtyMin,
  morning_start: parseHourTimeToTimestamp(DEFAULT_MORNING_RANGE[0]),
  morning_end: parseHourTimeToTimestamp(DEFAULT_MORNING_RANGE[1]),
  afternoon_start: parseHourTimeToTimestamp(DEFAULT_AFTERNOON_RANGE[0]),
  afternoon_end: parseHourTimeToTimestamp(DEFAULT_AFTERNOON_RANGE[1]),
  exam_switch: true,
  exam_score_limit: 60,
  virus_switch: true,
  system_disk_used_switch: false,
  system_disk_used: 90,
  hard_disk_used_switch: false,
  hard_disk_used: 90,
  cpu_used_switch: true,
  cpu_used: 90,
  cpu_used_duration: 50,
  memory_used_switch: true,
  memory_used: 90,
  memory_used_duration: 50,
  cpu_temperature_switch: true,
  cpu_temperature: 70,
  cpu_temperature_duration: 10,
  disk_temperature_switch: true,
  disk_temperature: 70,
  disk_temperature_duration: 10,
  net_up_speed_switch: true,
  net_up_speed: 70,
  net_up_speed_duration: 10,
  net_down_speed_switch: true,
  net_down_speed: 70,
  net_down_speed_duration: 10
}

/** i18n key 后缀，对应 groupPolicySettings.earlyWarnTitle* / earlyWarnDesc* */
export interface EarlyWarningRowMeta {
  titleKey: string
  switchKey: string
  valueKey: string
  descKey: string
  min: number
  max: number
  durationKey?: string
  durationDescKey?: string
  durationMin?: number
  durationMax?: number
}

export const EARLY_WARNING_ROW_META: readonly EarlyWarningRowMeta[] = [
  {
    titleKey: 'earlyWarnTitleExam',
    switchKey: 'exam_switch',
    valueKey: 'exam_score_limit',
    descKey: 'earlyWarnDescExam',
    min: 1,
    max: 99
  },
  {
    titleKey: 'earlyWarnTitleVirus',
    switchKey: 'virus_switch',
    valueKey: '',
    descKey: 'earlyWarnDescVirus',
    min: 0,
    max: 0
  },
  {
    titleKey: 'earlyWarnTitleSysDisk',
    switchKey: 'system_disk_used_switch',
    valueKey: 'system_disk_used',
    descKey: 'earlyWarnDescSysDisk',
    min: 70,
    max: 99
  },
  {
    titleKey: 'earlyWarnTitleDiskTotal',
    switchKey: 'hard_disk_used_switch',
    valueKey: 'hard_disk_used',
    descKey: 'earlyWarnDescDiskTotal',
    min: 70,
    max: 99
  },
  {
    titleKey: 'earlyWarnTitleCpu',
    switchKey: 'cpu_used_switch',
    valueKey: 'cpu_used',
    durationKey: 'cpu_used_duration',
    descKey: 'earlyWarnDescCpu',
    durationDescKey: 'earlyWarnDurationMin',
    min: 70,
    max: 99,
    durationMin: 10,
    durationMax: 29
  },
  {
    titleKey: 'earlyWarnTitleMem',
    switchKey: 'memory_used_switch',
    valueKey: 'memory_used',
    durationKey: 'memory_used_duration',
    descKey: 'earlyWarnDescMem',
    durationDescKey: 'earlyWarnDurationMin',
    min: 70,
    max: 99,
    durationMin: 10,
    durationMax: 29
  },
  {
    titleKey: 'earlyWarnTitleCpuTemp',
    switchKey: 'cpu_temperature_switch',
    valueKey: 'cpu_temperature',
    durationKey: 'cpu_temperature_duration',
    durationDescKey: 'earlyWarnDurationMin',
    descKey: 'earlyWarnDescCpuTemp',
    min: 70,
    max: 255,
    durationMin: 10,
    durationMax: 29
  },
  {
    titleKey: 'earlyWarnTitleDiskTemp',
    switchKey: 'disk_temperature_switch',
    valueKey: 'disk_temperature',
    durationKey: 'disk_temperature_duration',
    durationDescKey: 'earlyWarnDurationMin',
    descKey: 'earlyWarnDescDiskTemp',
    min: 70,
    max: 255,
    durationMin: 10,
    durationMax: 29
  },
  {
    titleKey: 'earlyWarnTitleNetUp',
    switchKey: 'net_up_speed_switch',
    valueKey: 'net_up_speed',
    durationKey: 'net_up_speed_duration',
    durationDescKey: 'earlyWarnDurationMin',
    descKey: 'earlyWarnDescNetUp',
    min: 5,
    max: 255,
    durationMin: 10,
    durationMax: 29
  },
  {
    titleKey: 'earlyWarnTitleNetDown',
    switchKey: 'net_down_speed_switch',
    valueKey: 'net_down_speed',
    durationKey: 'net_down_speed_duration',
    durationDescKey: 'earlyWarnDurationMin',
    descKey: 'earlyWarnDescNetDown',
    min: 5,
    max: 255,
    durationMin: 10,
    durationMax: 29
  }
]

export interface WarningData {
  title: string
  switchKey: string
  valueKey: string
  durationKey?: string
  durationDesc?: string
  desc: string
  min: number
  max: number
  durationMin?: number
  durationMax?: number
}

export function buildWarningRows (t: ComposerTranslation): WarningData[] {
  return EARLY_WARNING_ROW_META.map((row) => ({
    title: t(`groupPolicySettings.${row.titleKey}`),
    switchKey: row.switchKey,
    valueKey: row.valueKey,
    durationKey: row.durationKey,
    desc: t(`groupPolicySettings.${row.descKey}`),
    durationDesc: row.durationDescKey ? t(`groupPolicySettings.${row.durationDescKey}`) : undefined,
    min: row.min,
    max: row.max,
    durationMin: row.durationMin,
    durationMax: row.durationMax
  }))
}

export function promptSetting ({
  data,
  strategy,
  title,
  key,
  type,
  t
}: {
  data: WarningData[]
  strategy: Record<string, unknown>
  title?: string
  key?: string
  type?: string
  t: ComposerTranslation
}): void {
  if (!key || !title) {
    return
  }
  const setting = data.find(
    item => item.valueKey === key || item.durationKey === key
  )
  if (!setting) {
    return
  }
  let min = setting.min
  let max = setting.max
  let desc = t('groupPolicySettings.earlyWarnPromptRange', { min, max })

  if (type === 'duration') {
    if (setting.durationMin === undefined || setting.durationMax === undefined) {
      return
    }
    min = setting.durationMin
    max = setting.durationMax
    desc = t('groupPolicySettings.earlyWarnPromptDurationRange', { min, max })
  }

  ElMessageBox.prompt(desc, t('groupPolicySettings.earlyWarnPromptTitleTpl', { title }), {
    inputType: 'number',
    inputValue: String(strategy[key]),
    inputValidator: (value: string) => {
      const val = Number(value)

      if (val < min) {
        return false
      }

      if (val > max) {
        return false
      }

      return true
    },
    inputErrorMessage: t('groupPolicySettings.earlyWarnPromptInputError'),
    confirmButtonText: t('common.confirm'),
    showCancelButton: false
  })
    .then(({ value }: { value: string }) => {
      (strategy as Record<string, number>)[key] = Number(value)
    })
    .catch((err: any) => {
      console.log('prompt err:', err)
    })
}

export function setSliderData ({
  data,
  morningRange,
  afternoonRange,
  morningRangeNode,
  afternoonRangeNode
}: {
  data: EarlyWarningAndMonitoringSettings
  morningRange: Ref<number[]>
  afternoonRange: Ref<number[]>
  morningRangeNode: any
  afternoonRangeNode: any
}): void {
  if (data.morning_start !== undefined) {
    morningRange.value[0] = parseHourTime(data.morning_start)
    morningRangeNode.value.firstValue = morningRange.value[0]
  }
  if (data.morning_end !== undefined) {
    morningRange.value[1] = parseHourTime(data.morning_end)
    morningRangeNode.value.secondValue = morningRange.value[1]
  }
  if (data.afternoon_start !== undefined) {
    afternoonRange.value[0] = parseHourTime(data.afternoon_start)
    afternoonRangeNode.value.firstValue = afternoonRange.value[0]
  }
  if (data.afternoon_end !== undefined) {
    afternoonRange.value[1] = parseHourTime(data.afternoon_end)
    afternoonRangeNode.value.secondValue = afternoonRange.value[1]
  }
}
